import { PersonOutline, ShieldOutlined } from "@mui/icons-material";
import { Box, Stack } from "@mui/material";
import { COLORS } from "assets";
import { FormModalActions, MyModal, SelectInput, TextInput } from "components";
import { ENDPOINTS, USER_TYPES } from "config/constants";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import { useSnackbar } from "notistack";
import { FormEventHandler, useCallback, useEffect, useMemo, useState } from "react";
import { UserModel, UserType } from "types";
import { validateName } from "utils";

const defaultValue = {
  name: "",
  email: "",
  type: USER_TYPES[0]
};

const fieldSx = { height: 48, minHeight: 48 };
const iconSx = { fontSize: 20, color: COLORS.surface.secondaryText };

interface IEditUser {
  user: UserModel;
  onSuccess?: () => void;
  onClose: () => void;
}

export default function User({ user, onSuccess, onClose }: IEditUser) {
  const [state, setState] = useState({
    name: user.name,
    email: user.email,
    type: user.type
  });
  const [errors, setErrors] = useState(defaultValue);

  const t = useTranslation();
  const disabledSubmit = useMemo(() => !!(errors.name || !state.name || errors.email || !state.email), [state, errors]);
  const { isLoading, response, error, callFetch: callFetchPost } = useFetch("PUT");
  const { enqueueSnackbar } = useSnackbar();

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      const body: Record<string, string> = {};
      if (user.email !== state.email) {
        body.email = state.email;
      }
      if (user.type !== state.type) {
        body.type = state.type;
      }
      if (user.name !== state.name) {
        body.name = state.name;
      }
      if (Object.keys(body).length === 0) {
        enqueueSnackbar(t["Nothing to update"] || "Nothing to update", { variant: "info" });
        return;
      }
      callFetchPost(ENDPOINTS.users + `/${user._id}`, {
        body: JSON.stringify(body)
      }).catch(console.error);
    },
    [state, user, callFetchPost, enqueueSnackbar, t]
  );

  useEffect(() => {
    if (response) {
      enqueueSnackbar(response.message || "", { variant: "success" });
      onSuccess?.();
      onClose();
    }
  }, [response, onClose, enqueueSnackbar, onSuccess]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message || "", { variant: "error" });
      if (error.data?.errors) {
        setErrors((prevState) => ({
          ...prevState,
          ...error.data.errors
        }));
      }
    }
  }, [error, enqueueSnackbar]);

  return (
    <MyModal
      title={t["Edit User"]}
      subtitle={t["Edit User description"]}
      open={true}
      setOpen={onClose}
      variant="light"
    >
      <Box component="form" onSubmit={onSubmit}>
        <Stack spacing={2.5}>
          <TextInput
            value={state.name}
            label={t["Name"]}
            type="text"
            placeholder={t["e.g John Doe"]}
            error={!!errors.name}
            helperText={errors.name}
            startAdornment={<PersonOutline sx={iconSx} />}
            sx={fieldSx}
            onChange={(e) => {
              setState((prevState) => ({
                ...prevState,
                name: e.target.value
              }));
              setErrors((prevState) => ({
                ...prevState,
                name: validateName(e.target.value)
              }));
            }}
            fullWidth
            required
            autoFocus
          />
          <TextInput
            value={state.email}
            label={t["Email"]}
            type="email"
            placeholder={t["e.g johndoes@gmail.com"]}
            error={!!errors.email}
            helperText={errors.email}
            sx={fieldSx}
            onChange={(e) => {
              setState((prevState) => ({
                ...prevState,
                email: e.target.value
              }));
              setErrors((prevState) => ({
                ...prevState,
                email: e.target.value ? "" : t["Email Address required"]
              }));
            }}
            fullWidth
            required
          />
          <SelectInput
            id="type-select"
            data={USER_TYPES.map((item) => ({
              label: item,
              value: item
            }))}
            value={state.type}
            label={t["User Type"]}
            helperText=""
            startAdornment={<ShieldOutlined sx={iconSx} />}
            onChange={({ target: { value } }) => {
              if (!value) return;
              setState((prevState) => ({
                ...prevState,
                type: value as UserType
              }));
            }}
            fullWidth
          />
        </Stack>

        <FormModalActions
          onCancel={onClose}
          cancelLabel={t["Cancel"]}
          submitLabel={t["Update"]}
          loading={isLoading}
          disabled={disabledSubmit}
        />
      </Box>
    </MyModal>
  );
}
